import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FEATURES = ["Unlimited bookings & calendar", "Client files, photos & consult forms", "True-cost pricing & KPIs", "POS checkout & reports", "AI coach & assistant"];

export default function Billing() {
  const [me, setMe] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => { api.get("/billing/me").then(({ data }) => setMe(data)).catch(() => {}); api.get("/billing/usage").then(({ data }) => setUsage(data)).catch(() => {}); };
  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      api.get(`/billing/status/${sid}`).then(({ data }) => {
        if (data.status === "complete") toast.success("Subscription active — your 14-day trial has started!");
        setParams({}, { replace: true }); load();
      }).catch(() => load());
    } else { if (params.get("cancel")) toast.info("Checkout cancelled"); load(); }
    // eslint-disable-next-line
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start checkout"); setLoading(false); }
  };

  const manage = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open the billing portal"); setLoading(false); }
  };

  const active = me?.subscribed && ["trialing", "active"].includes(me?.status);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-8" data-testid="billing-page">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><CreditCard className="h-7 w-7 text-primary" />Billing</h1>
      <p className="text-muted-foreground mt-1">Your Azordi clinic subscription.</p>

      <Card className="rounded-2xl border p-6 shadow-sm mt-6">
        {me?.status === "comped" ? (
          <div data-testid="billing-comped">
            <div className="inline-flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="h-5 w-5" />Complimentary account</div>
            <p className="text-muted-foreground mt-2 text-sm">Your clinic is on a complimentary Azordi plan — no subscription or payment required. Enjoy the full suite. 🎉</p>
          </div>
        ) : active ? (
          <div data-testid="billing-active">
            <div className="inline-flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="h-5 w-5" />{me.status === "trialing" ? "Free trial active" : "Subscription active"}</div>
            <p className="text-muted-foreground mt-2 text-sm">Azordi Clinic Plan — $249/mo AUD.{me.trial_end ? ` Trial ends ${new Date(me.trial_end * 1000).toLocaleDateString("en-AU")}.` : ""}</p>
            <Button variant="outline" className="rounded-lg mt-5 h-11" onClick={manage} disabled={loading} data-testid="manage-subscription-button">
              <CreditCard className="h-4 w-4 mr-2" />{loading ? "Opening…" : "Manage subscription"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">Update your card, view invoices or cancel — via secure Stripe.</p>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="font-heading font-extrabold text-4xl">$249</span>
              <span className="text-muted-foreground">/month AUD</span>
            </div>
            <p className="text-sm text-primary font-medium mt-1">14-day free trial — card required, cancel anytime.</p>
            <ul className="mt-5 space-y-2">
              {FEATURES.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" />{f}</li>)}
            </ul>
            <Button className="w-full rounded-lg mt-6 h-12" onClick={subscribe} disabled={loading} data-testid="start-trial-button">
              <Sparkles className="h-4 w-4 mr-2" />{loading ? "Redirecting…" : "Start 14-day free trial"}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">Have a discount code? Enter it on the secure Stripe checkout page.</p>
          </>
        )}
      </Card>

      {usage && (
        <Card className="rounded-2xl border p-6 shadow-sm mt-6" data-testid="billing-usage">
          <h2 className="font-heading font-bold text-lg mb-1">Your plan & usage</h2>
          <p className="text-sm text-muted-foreground mb-4">Add-ons bill automatically on top of your base plan once your Stripe account is connected.</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Base plan</span><span className="tabular-nums">${usage.base_aud}/mo</span></div>
            <div className="flex justify-between">
              <span>Clinic staff <span className="text-muted-foreground">({usage.staff.used} used · {usage.staff.included} included, admin free)</span></span>
              <span className="tabular-nums">{usage.staff.extra > 0 ? `${usage.staff.extra} × $${usage.staff.each_aud} = $${usage.staff.cost_aud}/mo` : "included"}</span>
            </div>
            <div className="flex justify-between">
              <span>Locations <span className="text-muted-foreground">({usage.locations.used} used · {usage.locations.included} included)</span></span>
              <span className="tabular-nums">{usage.locations.extra > 0 ? `${usage.locations.extra} × $${usage.locations.each_aud} = $${usage.locations.cost_aud}/mo` : "included"}</span>
            </div>
            <div className="flex justify-between" data-testid="billing-sms-row">
              <span>SMS this month <span className="text-muted-foreground">({usage.sms.used} sent · {usage.sms.included} included)</span></span>
              <span className="tabular-nums">{usage.sms.over > 0 ? `${usage.sms.over} over × $${usage.sms.each_aud} = $${usage.sms.cost_aud}/mo` : "included"}</span>
            </div>
            {usage.sms.over > 0 && (
              <p className="text-[11px] text-muted-foreground -mt-1">Extra texts over your monthly {usage.sms.included} are billed at ${usage.sms.each_aud} each on your next invoice.</p>
            )}
            <div className="border-t border-border my-2" />
            <div className="flex justify-between font-heading font-bold text-base"><span>Total</span><span className="tabular-nums" data-testid="billing-total">${usage.total_monthly_aud}/mo</span></div>
          </div>
        </Card>
      )}
    </div>
  );
}
